import { createContext, useState, useCallback, useMemo } from "react";
import _ from "lodash";

type PaginationState = {
  page: number;
  history: [string | undefined, number][];
};

type TableState = {
  cursor?: string;
  filters?: Record<string, unknown>;
  pagination?: PaginationState;
};

type TablesState = Record<string, TableState>;

type TablesStateContextType = {
  getTableState: (tableKey: string) => TableState;
  setCursor: (tableKey: string, cursor: string | undefined) => void;
  setFilters: (tableKey: string, filters: Record<string, unknown>) => void;
  setPagination: (tableKey: string, pagination: PaginationState) => void;
  resetAllTables: () => void;
};

const TablesStateContext = createContext<TablesStateContextType>({
  getTableState: () => ({}),
  setCursor: () => {},
  setFilters: () => {},
  setPagination: () => {},
  resetAllTables: () => {},
});

const EMPTY_TABLE_STATE: TableState = Object.freeze({});

const TablesStateProvider = ({
  children,
}: React.PropsWithChildren): JSX.Element => {
  const [tablesState, setTablesState] = useState<TablesState>({});

  const getTableState = useCallback(
    (tableKey: string): TableState => tablesState[tableKey] ?? EMPTY_TABLE_STATE,
    [tablesState]
  );

  const setCursor = useCallback(
    (tableKey: string, cursor: string | undefined) => {
      setTablesState((prev) => {
        if (prev[tableKey]?.cursor === cursor) return prev;
        return {
          ...prev,
          [tableKey]: { ...prev[tableKey], cursor },
        };
      });
    },
    []
  );

  const setFilters = useCallback(
    (tableKey: string, filters: Record<string, unknown>) => {
      setTablesState((prev) => {
        const prevFilters = prev[tableKey]?.filters;
        if (_.isEqual(prevFilters, filters)) return prev;

        return {
          ...prev,
          [tableKey]: {
            ...prev[tableKey],
            filters,
            cursor: undefined,
            pagination: undefined,
          },
        };
      });
    },
    []
  );

  const setPagination = useCallback(
    (tableKey: string, pagination: PaginationState) => {
      setTablesState((prev) => {
        if (_.isEqual(prev[tableKey]?.pagination, pagination)) return prev;
        return {
          ...prev,
          [tableKey]: { ...prev[tableKey], pagination },
        };
      });
    },
    []
  );

  const resetAllTables = useCallback(() => {
    setTablesState({});
  }, []);

  const contextValue = useMemo(
    () => ({ getTableState, setCursor, setFilters, setPagination, resetAllTables }),
    [getTableState, setCursor, setFilters, setPagination, resetAllTables]
  );

  return (
    <TablesStateContext.Provider value={contextValue}>
      {children}
    </TablesStateContext.Provider>
  );
};

export default TablesStateContext;
export { TablesStateProvider };
